import { getMap } from '../content/maps';
import { PRODUCER_IDS } from '../content/production';
import type { MapId } from '../content/maps';
import type { Building, GameState, PlayerState, Projectile, Unit } from './types';

/*
 * המרת מצב לתמונת מצב וחזרה.
 *
 * זה עובד בדיוק משום שהמצב הוא אובייקט שטוח של מספרים שלמים: אין Date, אין מצביעים,
 * אין float שמתגלגל. מכאן מגיעים "המשך משחק", replay ובהמשך גם reconnect במולטיפלייר.
 *
 * **כל שדה חדש במצב חייב להיכנס גם לכאן וגם ל-hash.** שכחה נתפסת אוטומטית בטסט
 * הדטרמיניזם: שמירה באמצע → טעינה → המשך חייב להחזיר אותו hash כמו ריצה רצופה.
 *
 * שכבת העפרות נשמרת כדלתא מול המפה, ולא במלואה: ברוב המשחק היא זהה למפה, ואין
 * טעם לגרור 9,216 ערכים בכל שמירה.
 */

/*
 * 2: הוחלף בורר העמדות במצב יחידה מורחב ובעוגן רצועה (homeX/homeY).
 * 3: נוספו מבנים למצב, ומטען עפרות ליחידה.
 * 4: נוספו seed המשחק ורמת הקושי של כל שחקן — בלעדיהם היריב לא משוחזר.
 * 5: מפת `duel` גדלה מ-40x40 ל-56x56. **שינוי מידות מפה מחייב גרסה חדשה**:
 *    שכבת העפרות נשמרת כדלתא מול המפה, ואינדקס אריח מגרסה ישנה מצביע על מקום אחר.
 * 6: נוספה נקודת התכנסות לכל מבנה.
 * 11: נקודות הפתיחה מוגרלות בכל משחק ולכן נשמרות במצב. שמירה ישנה הייתה נטענת
 *     עם נקודות ריקות, ומזה בסיס בפינה (0,0) ו-AI שמכוון לשם.
 * שמירה מגרסה ישנה נדחית ברעש — הצורה של המצב השתנתה.
 */
export const SAVE_VERSION = 11;

/** דוחס מערך של 0/1 לאורכי ריצה לסירוגין, החל מריצה של אפסים. */
function runLengths(bytes: Uint8Array): number[] {
    const runs: number[] = [];
    let current = 0;
    let length = 0;
    for (const byte of bytes) {
        const value = byte === 0 ? 0 : 1;
        if (value === current) {
            length++;
            continue;
        }
        runs.push(length);
        current = value;
        length = 1;
    }
    runs.push(length);
    return runs;
}

/** ההפך מ-`runLengths`. אורך יעד מפורש, כדי ששמירה קטומה לא תיצור מערך קצר. */
function fromRunLengths(runs: readonly number[] | undefined, size: number): Uint8Array {
    const bytes = new Uint8Array(size);
    if (!runs) return bytes;
    let index = 0;
    let value = 0;
    for (const run of runs) {
        const end = Math.min(size, index + run);
        if (value === 1) bytes.fill(1, index, end);
        index = end;
        value = value === 0 ? 1 : 0;
        if (index >= size) break;
    }
    return bytes;
}

export interface SavedGame {
    readonly version: number;
    readonly mapId: string;
    /** זוגות [x, y] לכל שחקן — נקודות הפתיחה שהוגרלו למשחק הזה. */
    readonly startSlots: readonly number[];
    readonly tick: number;
    readonly rng: readonly [number, number, number, number];
    /** ה-seed שממנו נבנה המשחק. ה-AI נגזר ממנו, ובלעדיו הוא ימשיך אחרת. */
    readonly seed: number;
    readonly nextEntityId: number;
    readonly phase: number;
    readonly winner: number;
    /** [אינדקס, סוג, אשראי, מובס, קושי, יוצרו, אבדו] */
    readonly players: readonly (readonly [
        number,
        string,
        number,
        number,
        number,
        number,
        number,
    ])[];
    /** [שחקן, אינדקס-תור, סוג-פריט (מבנה/יחידה), סוג, שולם, מוכן] */
    readonly queues: readonly (readonly number[])[];
    readonly units: readonly (readonly number[])[];
    readonly buildings: readonly (readonly number[])[];
    readonly projectiles: readonly (readonly number[])[];
    readonly pathQueue: readonly number[];
    /** זוגות [אריח, כמות] שבהם העפרות שונות ממה שהמפה מגדירה. */
    readonly oreDelta: readonly number[];
    /**
     * הזיכרון של ערפל הקרב לכל שחקן, כ-RLE של אורכי ריצה לסירוגין החל מ-0.
     * ‏9,216 אריחים הם כמעט תמיד שני-שלושה גושים רציפים, ולכן זה כמה עשרות
     * מספרים ולא 9,216 — ומספיק דחוס כדי שהשמירה תישאר בקילובייטים.
     */
    readonly explored: readonly (readonly number[])[];
}

export function serialize(state: GameState): SavedGame {
    const baseline = getMap(state.mapId as MapId).ore;
    const oreDelta: number[] = [];
    for (let i = 0; i < state.ore.length; i++) {
        const amount = state.ore[i] ?? 0;
        if (amount !== (baseline[i] ?? 0)) oreDelta.push(i, amount);
    }

    return {
        version: SAVE_VERSION,
        mapId: state.mapId,
        startSlots: state.startSlots.flatMap((slot) => [slot.x, slot.y]),
        tick: state.tick,
        rng: [state.rng.a, state.rng.b, state.rng.c, state.rng.d],
        seed: state.seed,
        nextEntityId: state.nextEntityId,
        phase: state.phase,
        winner: state.winner,
        players: state.players.map(
            (p) =>
                [
                    p.index,
                    p.kind,
                    p.credits,
                    p.defeated ? 1 : 0,
                    p.difficulty,
                    p.unitsBuilt,
                    p.unitsLost,
                ] as const,
        ),
        queues: state.players.flatMap((p) =>
            p.queues.flatMap((queue, index) =>
                queue.map((i) => [p.index, index, i.kind, i.type, i.paid, i.ready ? 1 : 0]),
            ),
        ),
        explored: state.players.map((p) => runLengths(p.explored)),
        // מערך שטוח לכל יחידה: אורך המסלול בסוף, ואחריו האריחים.
        units: state.units.map((u) => [
            u.id,
            u.owner,
            u.type,
            u.x,
            u.y,
            u.hp,
            u.mode,
            u.goalTile,
            u.homeX,
            u.homeY,
            u.targetId,
            u.cooldown,
            u.burstLeft,
            u.cargo,
            u.path.length,
            ...u.path,
        ]),
        buildings: state.buildings.map((b) => [
            b.id,
            b.owner,
            b.type,
            b.tileX,
            b.tileY,
            b.hp,
            b.targetId,
            b.cooldown,
            b.burstLeft,
            b.rallyTile,
            b.repairing,
        ]),
        projectiles: state.projectiles.map((p) => [
            p.id,
            p.owner,
            p.type,
            p.x,
            p.y,
            p.targetId,
            p.lastX,
            p.lastY,
            p.damage,
            p.warhead,
        ]),
        pathQueue: [...state.pathQueue],
        oreDelta,
    };
}

export class SaveIncompatibleError extends Error {}

export function deserialize(saved: SavedGame): GameState {
    // נדחה ברעש ולא נטען בשקט לתוך משחק אחר.
    if (saved.version !== SAVE_VERSION) {
        throw new SaveIncompatibleError(
            `שמירה בגרסה ${saved.version}, המשחק בגרסה ${SAVE_VERSION}`,
        );
    }

    const map = getMap(saved.mapId as MapId);
    const ore = Uint16Array.from(map.ore);
    for (let i = 0; i + 1 < saved.oreDelta.length; i += 2) {
        const index = saved.oreDelta[i] ?? 0;
        ore[index] = saved.oreDelta[i + 1] ?? 0;
    }

    const units: Unit[] = saved.units.map((raw) => {
        const pathLength = raw[14] ?? 0;
        return {
            id: raw[0] ?? 0,
            owner: raw[1] ?? 0,
            type: raw[2] ?? 0,
            x: raw[3] ?? 0,
            y: raw[4] ?? 0,
            hp: raw[5] ?? 0,
            mode: (raw[6] ?? 0) as Unit['mode'],
            goalTile: raw[7] ?? -1,
            homeX: raw[8] ?? 0,
            homeY: raw[9] ?? 0,
            targetId: raw[10] ?? -1,
            cooldown: raw[11] ?? 0,
            burstLeft: raw[12] ?? 0,
            cargo: raw[13] ?? 0,
            path: raw.slice(15, 15 + pathLength),
        };
    });

    const buildings: Building[] = saved.buildings.map((raw) => ({
        id: raw[0] ?? 0,
        owner: raw[1] ?? 0,
        type: raw[2] ?? 0,
        tileX: raw[3] ?? 0,
        tileY: raw[4] ?? 0,
        hp: raw[5] ?? 0,
        targetId: raw[6] ?? -1,
        cooldown: raw[7] ?? 0,
        burstLeft: raw[8] ?? 0,
        rallyTile: raw[9] ?? -1,
        repairing: raw[10] ?? 0,
    }));

    const projectiles: Projectile[] = saved.projectiles.map((raw) => ({
        id: raw[0] ?? 0,
        owner: raw[1] ?? 0,
        type: raw[2] ?? 0,
        x: raw[3] ?? 0,
        y: raw[4] ?? 0,
        targetId: raw[5] ?? -1,
        lastX: raw[6] ?? 0,
        lastY: raw[7] ?? 0,
        damage: raw[8] ?? 0,
        warhead: raw[9] ?? 0,
    }));

    const players: PlayerState[] = saved.players.map((raw) => {
        const index = raw[0];
        const rows = saved.queues.filter((q) => q[0] === index);
        return {
            index,
            kind: raw[1] as PlayerState['kind'],
            credits: raw[2],
            defeated: raw[3] === 1,
            difficulty: raw[4],
            unitsBuilt: raw[5],
            unitsLost: raw[6],
            explored: fromRunLengths(saved.explored?.[index], map.w * map.h),
            queues: PRODUCER_IDS.map((_id, queueIndex) =>
                rows
                    .filter((q) => q[1] === queueIndex)
                    .map((q) => ({
                        kind: q[2] ?? 0,
                        type: q[3] ?? 0,
                        paid: q[4] ?? 0,
                        ready: q[5] === 1,
                    })),
            ),
        };
    });

    return {
        tick: saved.tick,
        rng: {
            a: saved.rng[0],
            b: saved.rng[1],
            c: saved.rng[2],
            d: saved.rng[3],
        },
        seed: saved.seed,
        nextEntityId: saved.nextEntityId,
        mapId: saved.mapId,
        startSlots: Array.from({ length: Math.floor(saved.startSlots.length / 2) }, (_v, i) => ({
            x: saved.startSlots[i * 2] ?? 0,
            y: saved.startSlots[i * 2 + 1] ?? 0,
        })),
        ore,
        units,
        buildings,
        projectiles,
        players,
        phase: saved.phase as GameState['phase'],
        winner: saved.winner,
        pathQueue: [...saved.pathQueue],
        // אירועים הם פלט של טיק בודד ואינם נשמרים — ראו ההערה ליד SimEvent.
        events: [],
    };
}
